import { GlobalSearchModal, IGlobalSearchResultGroup, IGlobalSearchSuggestionGroup } from 'odc-shared/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ArticlesCard from '@src/components/ArticlesCard';
import DocsCard, { DocType } from '@src/components/DocsCard';
import HelpCenterBanner from '@src/components/HelpCenterBanner';
import useMobxSelector from '@src/hooks/useMobxSelector';

import { SupportHomeSearchState } from '../hooks';

interface SupportHomeProps {
  onViewMore: () => void;
  searchSuggestions: IGlobalSearchSuggestionGroup[];
  keyword: string;
  searchState: SupportHomeSearchState;
  onKeywordChange: (keyword: string) => void;
  showGlobalSearch: boolean;
  setShowGlobalSearch: (show: boolean) => void;
}

const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1180;

const SupportHome = (props: SupportHomeProps) => {
  const {
    onViewMore,
    searchSuggestions,
    keyword,
    searchState,
    onKeywordChange,
    showGlobalSearch,
    setShowGlobalSearch,
  } = props;
  const [bannerRectTop, setBannerRectTop] = useState<number>(0);
  const helpCenterBannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const getBannerRectTop = () => {
      if (helpCenterBannerRef.current) {
        const bannerTop = helpCenterBannerRef.current.getBoundingClientRect().top;
        setBannerRectTop(bannerTop);
      }
    };
    window.addEventListener('resize', getBannerRectTop);
    getBannerRectTop();
    return () => {
      window.removeEventListener('resize', getBannerRectTop);
    };
  }, []);

  // 从store获取热门文章数据
  const popularPosts = useMobxSelector((stores) => stores.helpCenterStore.popularPosts);

  // 转换搜索结果为组件需要的格式
  const searchResults = useMemo((): IGlobalSearchResultGroup[] => {
    if (searchState.results.length === 0) return [];

    // 按平台分组
    const publisherResults = searchState.results.filter(post => post.platform === 'publisher');
    const advertiserResults = searchState.results.filter(post => post.platform === 'advertiser');

    const groups: IGlobalSearchResultGroup[] = [];

    if (advertiserResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = publisherResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'User Growth',
        results: advertiserResults.slice(0, limit).map((post, index) => ({
          id: `result_advertiser_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=advertiser`,
        })),
      });
    }

    if (publisherResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = advertiserResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'Monetization',
        results: publisherResults.slice(0, limit).map((post, index) => ({
          id: `result_publisher_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=publisher`,
        })),
      });
    }

    return groups;
  }, [searchState.results]);

  // 处理搜索框变化
  const handleSearchChange = useCallback((_e: unknown, value: string) => {
    onKeywordChange(value);
  }, [onKeywordChange]);

  // 处理搜索框关闭
  const handleSearchClose = useCallback(() => {
    setShowGlobalSearch(false);
    onKeywordChange('');
  }, [onKeywordChange, setShowGlobalSearch]);

  // 处理查看更多
  const handleViewMore = useCallback(() => {
    onViewMore();
  }, [onViewMore]);

  return (
    <div className="odc-page-support">
      <HelpCenterBanner
        style={{
          opacity: showGlobalSearch ? 0 : 1,
        }}
        onSearchClick={() => {
          setShowGlobalSearch(true);
        }}
        ref={(ref) => {
          helpCenterBannerRef.current = ref;
        }}
      />
      <div className="docs-card-group">
        <DocsCard type={DocType.UserGrowth} />
        <DocsCard type={DocType.Monetization} />
      </div>
      <div className="popular-card-group">
        {
          popularPosts.length > 0 && (
            <>
              <p className="text-base font-medium !mb-2r">Popular articles</p>
              <div className="article-card-group">
                {
                  popularPosts.map((post, index) => (
                    <ArticlesCard
                      key={post.postId || index}
                      title={post.name || 'Untitled'}
                      url={`/supportcenter?pid=${post.postId}&platform=${post.platform}`}
                      content={post.description || 'No description available'}
                    />
                  ))
                }
              </div>
            </>
          )
        }
      </div>
      <GlobalSearchModal
        title="How can we help you today?"
        placeholder="Search by topic or keyword"
        show={showGlobalSearch}
        value={keyword}
        suggestions={searchSuggestions}
        results={searchResults}
        resultsCount={searchState.totalCount}
        onChange={handleSearchChange}
        onClose={handleSearchClose}
        onViewMore={handleViewMore}
        style={!isMobile && bannerRectTop > 0 ? { alignItems: 'flex-start' } : {}}
        contentStyle={!isMobile && bannerRectTop > 0 ? { marginTop: bannerRectTop } : {}}
      />
    </div>
  );
};

export default SupportHome;
